// Skeleton — token-driven loading placeholders.
//
// Batch-3 C5 (QA finding P1): the SPA rendered a ~1s blank (court
// watermark only) while the profile / Discover API resolved, which reads
// as "broken," especially on mobile. These placeholders fill that gap
// with a calm pulse that mirrors the real layout instead of an empty
// screen.
//
// A11y: each skeleton root is a single `role="status"` live region with an
// sr-only "Loading…" so a screen reader announces the load once; the
// visual pulse blocks are presentational (aria-hidden) so SR users don't
// hear a tree of empty boxes. Tokens (hoop-surface / hoop-border) keep it
// correct in both themes.

interface BlockProps {
  className?: string
}

// A single pulsing bar/box. Compose these into shapes.
export function SkeletonBlock({ className = '' }: BlockProps) {
  return <div className={`animate-pulse rounded bg-hoop-border ${className}`} />
}

// A bordered "card" placeholder with a title bar + a few text lines, so
// the loading state has the same rhythm as a real profile card.
function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="bg-hoop-surface border border-hoop-border rounded-2xl p-6">
      <SkeletonBlock className="h-3.5 w-1/3 mb-4" />
      <div className="space-y-2.5">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonBlock
            key={i}
            className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`}
          />
        ))}
      </div>
    </div>
  )
}

// Full-page placeholder for /players/:slug while the profile loads.
// Mirrors the hero + two-column composition of the real page.
export function ProfileSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading profile"
      className="max-w-[1240px] mx-auto px-6 py-10 space-y-6"
    >
      <span className="sr-only">Loading…</span>
      <div aria-hidden="true" className="space-y-6">
        {/* Hero */}
        <div className="bg-hoop-surface border border-hoop-border rounded-2xl p-6 sm:p-7">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <SkeletonBlock className="w-24 h-24 rounded-2xl flex-shrink-0" />
            <div className="flex-1 w-full space-y-3">
              <SkeletonBlock className="h-7 w-1/2" />
              <div className="flex gap-2">
                <SkeletonBlock className="h-5 w-14 rounded-md" />
                <SkeletonBlock className="h-5 w-24 rounded-md" />
              </div>
              <SkeletonBlock className="h-3.5 w-2/3" />
            </div>
            <div className="hidden sm:flex flex-col gap-2 w-[200px]">
              <SkeletonBlock className="h-9 w-full rounded-md" />
              <SkeletonBlock className="h-9 w-full rounded-md" />
            </div>
          </div>
        </div>
        {/* Two-column body */}
        <div className="grid md:grid-cols-[312px_1fr] gap-6">
          <div className="space-y-4 order-2 md:order-1">
            <SkeletonCard lines={3} />
            <SkeletonCard lines={2} />
          </div>
          <div className="space-y-4 order-1 md:order-2">
            <SkeletonCard lines={3} />
            <SkeletonCard lines={5} />
          </div>
        </div>
      </div>
    </div>
  )
}

// A single player-card placeholder matching PlayerCard's footprint.
function SkeletonPlayerCard() {
  return (
    <div className="bg-hoop-surface border border-hoop-border rounded-lg overflow-hidden">
      <SkeletonBlock className="aspect-[4/3] w-full rounded-none" />
      <div className="p-4 space-y-2">
        <SkeletonBlock className="h-4 w-3/4" />
        <SkeletonBlock className="h-3 w-1/2" />
      </div>
    </div>
  )
}

// Grid of player-card placeholders for /discover + /saved while results
// load. `count` defaults to a full first page's worth.
export function PlayerCardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div role="status" aria-label="Loading players">
      <span className="sr-only">Loading…</span>
      <div
        aria-hidden="true"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonPlayerCard key={i} />
        ))}
      </div>
    </div>
  )
}
